/**
 * The joint dead-configuration rule: one rule for every Joint2D and Joint3D subclass.
 * It ports the `_update_joint` cases the scene text decides (joint_3d.cpp:76-88,
 * scene/2d/physics/joints/joint_2d.cpp:78-91). The "must be a PhysicsBody" cases
 * need the target's type, which an instanced sub-scene hides, so they are not ported.
 */
import { extractNodePath } from '../../../../linter/linterUtils.js';
import { descendsFromClass } from '../../../../godot/classdb/classBaseTypes.js';
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { resolveNodePath } from '../../../../linter/nodePathResolve.js';
import { armDiagnostic, armEmits, groundedArm } from '../../../../linter/ruleArms.js';
// Dimension-free: `emits` is rule-level, so a per-dimension name would put
// `joint3d-not-connected` in every PinJoint2D sheet. The dimension is
// already on the diagnostic's `nodeType` and in its message.
const arms = {
    notConnected: groundedArm('joint-not-connected', { kind: 'configuration-warning' }),
    sameBody: groundedArm('joint-same-body', { kind: 'configuration-warning' }),
};
/** `'2D'` or `'3D'` for a joint type, or undefined when it is not a joint. */
function jointDim(nodeType) {
    if (descendsFromClass(nodeType, 'Joint2D'))
        return '2D';
    if (descendsFromClass(nodeType, 'Joint3D'))
        return '3D';
    return undefined;
}
function checkJoint(context) {
    const { node } = context;
    const dim = jointDim(node.type);
    if (!dim)
        return [];
    const props = node.rawProperties;
    const bodyType = `PhysicsBody${dim}`;
    // An empty NodePath, Godot's "not connected", is absent. `body_a` is
    // `cast_to<PhysicsBody2D>(get_node_or_null(a))` (joint_2d.cpp:70-74, joint_3d.cpp:70-74),
    // so a `missing` target is unset too. An `unknowable` one may live in a sub-scene.
    // The resolved node is kept because the same-body arm compares pointers.
    const connected = (raw) => {
        const path = raw ? extractNodePath(raw) : null;
        if (path === null)
            return null;
        const target = resolveNodePath(context.scene, node, path);
        if (target.status === 'missing')
            return null;
        return target.status === 'found' ? { path, node: target.node } : { path };
    };
    const a = connected(props.node_a);
    const b = connected(props.node_b);
    // `body_a == body_b` (joint_2d.cpp:86) compares pointers, so `../Body` and `%Body`
    // naming one node are one body. Identical strings match even when `unknowable`.
    const sameBody = Boolean(a && b && (a.path === b.path || (a.node !== undefined && a.node === b.node)));
    // Exclusive, as in Godot's own chain: an unset end is reported once, and the
    // same-body case cannot arise while an end is unset. 2D warns on one unset end
    // (joint_2d.cpp:84-85). 3D warns only when both are unset (joint_3d.cpp:82-83).
    const unconnected = dim === '2D' ? !a || !b : !a && !b;
    if (unconnected) {
        const which = !a && !b ? "'node_a' and 'node_b' are" : `'${!a ? 'node_a' : 'node_b'}' is`;
        const howMany = dim === '2D' ? 'two' : 'any';
        return [
            armDiagnostic(arms.notConnected, node, `${node.type} '${node.name}' is not connected to ${howMany} ${bodyType}s: ${which} unset, so the joint does nothing.`),
        ];
    }
    if (sameBody) {
        return [
            armDiagnostic(arms.sameBody, node, `${node.type} '${node.name}' has 'node_a' and 'node_b' both pointing at ${a.node?.name ?? a.path}. A joint must connect two different ${bodyType}s.`),
        ];
    }
    return [];
}
const jointValidationRule = {
    meta: {
        name: 'valid-joint',
        description: 'Flags a joint that cannot form a constraint',
        category: 'validation',
        applicableNodeTypeMatcher: (nodeType) => jointDim(nodeType) !== undefined,
        emits: armEmits(arms),
    },
    check: checkJoint,
};
ruleRegistry.register(jointValidationRule);
//# sourceMappingURL=linter.js.map